import React, { useMemo } from "react";
import { differenceInDays } from "date-fns";
import PropTypes from "prop-types";

import {
  CheckOutlined as CheckOutlinedIcon,
  ErrorOutlineOutlined as ErrorOutlineOutlinedIcon,
} from "@mui/icons-material";

import CustomChip from "components/common/Chip";

import { LeaveRequest } from "components/Screens/Timeoff/timeoff.types";

interface RegulationChipProps {
  leaveRequest: LeaveRequest;
  type: string;
  noOfDays?: number;
}

interface ChipDetails {
  type: "default" | "success" | "warning";
  daysDifferenceLabel: string;
  daysDifference: number;
}

const getChipDetailsForRegulations = (
  timeoffType: string,
  noOfDays: number,
  leaveRequest: LeaveRequest
): ChipDetails => {
  if (timeoffType !== "Notice Period") {
    return { type: "default", daysDifferenceLabel: "", daysDifference: 0 };
  }

  const daysDifference = differenceInDays(new Date(leaveRequest?.selectedDateRange?.startDate), new Date()) + 1;

  const daysDifferenceLabel = `${daysDifference} day${daysDifference === 1 ? "" : "s"}`;

  return {
    type: daysDifference > noOfDays ? "success" : "warning",
    daysDifferenceLabel,
    daysDifference,
  };
};

function RegulationChip({ leaveRequest, type, noOfDays }: RegulationChipProps) {
  const chipDetails = useMemo(
    () => getChipDetailsForRegulations(type, noOfDays || 0, leaveRequest),
    [type, noOfDays, leaveRequest]
  );

  if (!noOfDays || !chipDetails.daysDifferenceLabel) {
    return null;
  }

  const isSuccess = chipDetails.type === "success";
  const statusLabel = isSuccess ? "Notice period requirement met" : "Notice period requirement not met";

  return (
    <div className="ml-1 mb-05">
      <CustomChip
        label={chipDetails.daysDifferenceLabel}
        color={chipDetails.type}
        labelClass=""
        size="small"
        onClick={() => {}}
        onDelete={() => {}}
        icon={isSuccess ? <CheckOutlinedIcon /> : <ErrorOutlineOutlinedIcon />}
        aria-label={statusLabel}
      />
    </div>
  );
}

RegulationChip.propTypes = {
  leaveRequest: PropTypes.shape({
    selectedDateRange: PropTypes.shape({
      startDate: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  type: PropTypes.string.isRequired,
  noOfDays: PropTypes.number,
};

export default RegulationChip;
